import { type AxiosResponse } from 'axios';
import { push } from 'connected-react-router';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import type Plan from '~/types/Plan';

import { ActionKeys } from '~/actions/constants';
import * as plansActions from '~/actions/plans.actions';
import * as userActions from '~/actions/user.actions';
import { PRO_PLAN_TRIAL_STARTED, USER_PLAN_PUSHED_EVENT } from '~/constants/gtm';
import { WIX_PRO_PLAN_NOTIFICATION_DISMISSED } from '~/constants/local-storage-keys';
import { PRO_PLAN } from '~/constants/plans';
import { type PlanCode } from '~/modules/onboarding/types/onboarding.type';
import { selectState } from '~/sagas/index';
import { getCurrentPlan } from '~/selectors/plans.selectors';
import { getDaysSinceJoined, userHasValidBilling } from '~/selectors/user.selectors';
import { sendGTMEvent, type GTMEventPayload } from '~/services/gtm.service';
import * as plansService from '~/services/plans.service';
import * as settingsService from '~/services/settings.service';
import { type UserPlan } from '~/types/Plan';
import { fromJSON, userPlanFromJSON } from '~/types/Plan';
import { track as optimizelyTrack } from '~/utils/featureflags/track';
import { FeatureFlagEvents } from '~/utils/featureflags/types';
import { FullStoryEvent, fullStoryTracking } from '~/utils/fullstory/tracking';
import { saveInLocalStorage } from '~/utils/local-storage';
import { observability } from '~/utils/observability/observability';
import { planToTrackingDetails } from '~/utils/plans.util';
import { Events, track } from '~/utils/segment';

export function* fetchUserPlans() {
  try {
    yield put(userActions.fetchUserEntitlements());

    const response: AxiosResponse = yield call(plansService.getUserPlans);
    const userPlans: UserPlan[] = response.data.results.map(userPlanFromJSON);
    yield put(plansActions.fetchUserPlansSuccess(userPlans));
  } catch (e) {
    yield put(plansActions.fetchUserPlansError(e.toString()));
  }
}

export function* fetchPricingPlans() {
  try {
    const response: AxiosResponse = yield call(plansService.getPricingPlans);
    const pricingPlans: Plan[] = response.data.results.map(fromJSON);
    yield put(plansActions.fetchPricingPlansSuccess(pricingPlans));
  } catch (e) {
    yield put(plansActions.fetchPricingPlansError(e.toString()));
  }
}

function* pushPlanToGTM() {
  const currentPlan: Maybe<UserPlan> = yield select(getCurrentPlan);
  const payload = {
    userIsPro: false,
  };

  if (currentPlan) payload.userIsPro = currentPlan.plan.isPro || currentPlan.plan.isFullAccess;

  const gtmEvent: GTMEventPayload = {
    data: payload,
    eventName: USER_PLAN_PUSHED_EVENT,
  };

  yield call(sendGTMEvent, gtmEvent);
}

export function* updateUserPlan(action: ReturnType<typeof plansActions.updateUserPlan>) {
  try {
    const daysSinceJoined = yield selectState(getDaysSinceJoined);
    const hasPaymentMethod = yield selectState(userHasValidBilling);

    const currentPlan: Maybe<UserPlan> = yield selectState<Maybe<UserPlan>>(getCurrentPlan);

    const response: AxiosResponse = yield call(plansService.setUserPlan, action.payload.planName);
    const userPlan: UserPlan = userPlanFromJSON(response.data);

    if (action.payload.planName === PRO_PLAN && userPlan.plan.isFreeTrial) {
      yield call(optimizelyTrack, FeatureFlagEvents.PRO_PLAN_TRIAL_STARTED, {
        days_since_joined: daysSinceJoined,
        has_payment_method: hasPaymentMethod,
      });
      yield call(sendGTMEvent, { eventName: PRO_PLAN_TRIAL_STARTED });
    }

    fullStoryTracking(FullStoryEvent.USER_PLAN_UPGRADED, {
      plan: action.payload.planName as PlanCode,
    });

    yield call(track, Events.plan_updated, {
      operation: 'started',
      ...planToTrackingDetails(userPlan),
      entry_point: action.payload.entryPoint,
      previous_plan_name: currentPlan?.plan?.displayName,
    });

    yield put(plansActions.updateUserPlanSuccess(userPlan, 'upgrade'));
  } catch (e) {
    yield put(plansActions.updateUserPlanError(e.toString()));
  }
}

function* closeWixProPlanNotification() {
  yield call(saveInLocalStorage, WIX_PRO_PLAN_NOTIFICATION_DISMISSED, 'true');
}

export function* submitCloseAccountForm(
  action: ReturnType<typeof plansActions.submitCloseAccountForm>,
) {
  const { auth, reason, tellUsMore } = action.payload;
  const authType = 'password' in auth ? 'password' : 'sso';

  observability.startEvent('CLOSE_ACCOUNT_EVENT', {
    preservedAttributes: { type: authType },
  });

  const payload: Record<string, string> = {
    churn_reason: reason,
  };
  if (tellUsMore !== '') payload.extra_info = tellUsMore;

  if ('password' in auth) {
    payload.password = auth.password;
  } else {
    payload.auth_code = auth.auth_code;
    payload.state = auth.state;
    payload.provider = auth.provider;
  }

  try {
    const response = yield call([settingsService, settingsService.closeAccount], payload);
    if (response?.status === 200) {
      observability.successEvent('CLOSE_ACCOUNT_EVENT');
      yield put(plansActions.closeAccountSuccess('full'));
    } else if (response?.status === 201) {
      observability.successEvent('CLOSE_ACCOUNT_EVENT');
      yield put(plansActions.closeAccountSuccess('partial'));
    } else {
      const errorMessage = response?.data?.error_type ?? 'account.plan.closeAccount.form.apiError';
      observability.errorEvent('CLOSE_ACCOUNT_EVENT', { errorMessage });
      if (response?.data?.error_type?.toUpperCase() === 'UNPAID_INVOICE') {
        yield put(plansActions.closeAccountError('account.plan.closeAccount.modal.unpaidInvoice'));
      } else if (response?.data?.error_type?.toUpperCase() === 'NON_PRIMARY_USER') {
        yield put(
          plansActions.closeAccountError('account.plan.closeAccount.modal.nonAccountOwner'),
        );
      } else if (response?.data?.error_type?.toUpperCase() === 'INVALID_PASSWORD') {
        yield put(
          plansActions.closeAccountError('account.plan.closeAccount.modal.invalidPassword'),
        );
      } else {
        yield put(plansActions.closeAccountError('account.plan.closeAccount.form.apiError'));
      }
    }
  } catch (err) {
    const errorMessage = err?.message ?? 'Close account request failed';
    observability.errorEvent('CLOSE_ACCOUNT_EVENT', { errorMessage });
    yield put(plansActions.closeAccountError('account.plan.closeAccount.form.apiError'));
  }
}

function* closeAccountSuccess(action: ReturnType<typeof plansActions.closeAccountSuccess>) {
  try {
    yield put(push(`/close?closeStatus=${action.payload.closeStatus}`));
  } catch (e) {
    yield put(plansActions.closeAccountError('account.plan.closeAccount.form.apiError'));
  }
}

export default [
  takeLatest(ActionKeys.FETCH_USER_PLANS, fetchUserPlans),
  takeLatest(ActionKeys.UPDATE_USER_PLAN_SUCCESS, fetchUserPlans),
  takeLatest(ActionKeys.FETCH_USER_PLANS_SUCCESS, pushPlanToGTM),
  takeLatest(ActionKeys.FETCH_PRICING_PLANS, fetchPricingPlans),
  takeLatest(ActionKeys.UPDATE_USER_PLAN, updateUserPlan),
  takeLatest(ActionKeys.CLOSE_WIX_PRO_PLAN_NOTIFICATION, closeWixProPlanNotification),
  takeLatest(ActionKeys.SUBMIT_CLOSE_ACCOUNT_FORM, submitCloseAccountForm),
  takeLatest(ActionKeys.CLOSE_ACCOUNT_SUCCESS, closeAccountSuccess),
];
